import { AuthRequest, Method } from 'src/util/Request';
import { baseUrl, genericNetworkErrorHandler } from '.';
import { QueryKey, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
    ShippingPortalParcel,
    ShippingPortalPrintItem,
    ShippingPortalFilters,
    ShippingPortalParcelWithLocationsAsIds,
    UserPreferences,
    NestedKeyOf,
    ShippingPortalLabelSizes,
} from '@lanterds/types';
import { ShippingPortalDataItem } from 'src/components/Views/ShippingPortal/types';
import { ShippingPortalFavorite } from 'src/util/types';

export enum ShippingLabelTypes { //TODO move to Types repo
    Return = 'return',
    Outbound = 'outbound',
    Transfer = 'transfer',
}

enum DataSet {
    Unmanifested = 'unmanifested',
    Manifested = 'manifested',
}

enum UrlTypes { //TODO move to Types repo
    Unmanifested = '/shippingPortal/parcel/unmanifested/search',
    Manifested = '/shippingPortal/manifest/search',
}

export const getManifestedOrUnmanifestedData = (
    dataSet: DataSet,
    filters?: ShippingPortalFilters,
    queryKey?: QueryKey,
    enabled = true,
) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    let uri = '';

    switch (dataSet) {
        case DataSet.Unmanifested:
            uri = UrlTypes.Unmanifested;
            break;
        case DataSet.Manifested:
            uri = UrlTypes.Manifested;
            break;
        default:
            break;
    }

    const results = useInfiniteQuery({
        queryKey: queryKey ?? [],
        queryFn: async ({ pageParam }) => {
            const body = { filter: filters, pageSize: filters?.pageSize, pageNumber: pageParam };
            const res = await AuthRequest(`${baseUrl}${uri}`, Method.POST, headers, body);

            if (res.ok) {
                const parcels = await res.json();
                return parcels;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            if ((lastPage as ShippingPortalDataItem[]).length === 0) {
                return undefined;
            }
            return lastPageParam + 1;
        },
        getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
            if (firstPageParam <= 1) {
                return undefined;
            }
            return firstPageParam - 1;
        },
        throwOnError: true,
        enabled,
    });
    return results;
};

export const getParcelById = (id: string, isEnabled = true) => {
    const url = `${baseUrl}/shippingPortal/parcel/${id}`;
    const results = useQuery({
        queryKey: [url, id],
        queryFn: async () => {
            const res = await AuthRequest(url, Method.GET);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
        enabled: isEnabled,
    });
    return results;
};

export const getNewManifestGroupsByParcelIds = async (parcelIds: number[]) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    const res = await AuthRequest(`${baseUrl}/shippingPortal/parcel/nonManifestedGroups`, Method.POST, headers, { parcelIds });
    if (res.ok) {
        const parcels = await res.json();
        return parcels;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const getManifestById = async (id: string) => {
    const url = `${baseUrl}/shippingPortal/manifest/${id}`;
    const res = await AuthRequest(url, Method.GET);

    if (res.ok) {
        const details = await res.json();
        return details;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const destroyManifest = async (id: string) => {
    const url = `${baseUrl}/shippingPortal/manifest/${id}`;
    const res = await AuthRequest(url, Method.DELETE);

    if (res.ok) {
        const details = await res.json();
        return details;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const generateManifest = async (parcelIds: string[], departureDate: string, note: string) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    const body = { parcelIds, departureDate, note };
    const res = await AuthRequest(`${baseUrl}/shippingPortal/manifest`, Method.POST, headers, body);

    if (res.ok) {
        const manifest = await res.json();
        return manifest;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const DEBUG_CLEAR_LOCAL_STORAGE = async () => {
    const res = await AuthRequest(`${baseUrl}/DEBUG/shippingPortal/clear`, Method.POST);
    if (res.ok) {
        const parcels = await res.json();
        return parcels;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const generateParcel = async (parcels: ShippingPortalParcel[]) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const mappedParcels = parcels.map<ShippingPortalParcelWithLocationsAsIds>((parcel) => {
        const mappedParcel: ShippingPortalParcelWithLocationsAsIds = {
            ...parcel,
            destinationId: parcel.destination.id,
            originId: parcel.origin.id,
            destination: undefined,
            origin: undefined,
        };
        return mappedParcel;
    });

    const res = await AuthRequest(`${baseUrl}/shippingPortal/parcel`, Method.POST, headers, { parcels: mappedParcels });
    if (res.ok) {
        const parcels = await res.json();
        return parcels;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const generateOutboundParcelPdfs = async (parcels: ShippingPortalParcel[]) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const mappedParcels = parcels.map<ShippingPortalParcelWithLocationsAsIds>((parcel) => {
        const mappedParcel: ShippingPortalParcelWithLocationsAsIds = {
            ...parcel,
            destinationId: parcel.destination.id,
            originId: parcel.origin.id,
            destination: undefined,
            origin: undefined,
        };
        return mappedParcel;
    });

    const res = await AuthRequest(`${baseUrl}/shippingPortal/outbound/parcels`, Method.POST, headers, {
        parcels: mappedParcels,
    });
    if (res.ok) {
        const pdf = await res.json();
        return pdf;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const generatePDF = async (body: ShippingPortalPrintItem[]) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const url = `${baseUrl}/shippingPortal/print`;
    const res = await AuthRequest(url, Method.POST, headers, body);

    if (res.ok) {
        const details = await res.json();
        return details;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const searchEndpoints = async (filters?: any, context?: any) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    const res = await AuthRequest(`${baseUrl}/shippingPortal/locations/search`, Method.POST, headers, { filters, context });
    if (res.ok) {
        const parcels = await res.json();
        return parcels;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const getParcelTypesByCustomer = async (customerFmtpId: string) => {
    const res = await AuthRequest(`${baseUrl}/shippingPortal/parcelTypes?customerId=${customerFmtpId}`, Method.GET);
    if (res.ok) {
        const types = await res.json();
        return types;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const deleteParcels = async (parcelIds: string[]) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const res = await AuthRequest(`${baseUrl}/shippingPortal/parcels`, Method.DELETE, headers, { parcelIds });
    if (res.ok) {
        const types = await res.json();
        return types;
    } else {
        await genericNetworkErrorHandler(res);
    }
};

export const getFavorites = (isEnabled = true) => {
    const url = `${baseUrl}/shippingPortal/favorites`;
    const results = useQuery({
        queryKey: [url],
        queryFn: async () => {
            const res = await AuthRequest(url, Method.GET);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
        enabled: isEnabled,
    });
    return results;
};

export const createFavorite = () => {
    const url = `${baseUrl}/shippingPortal/favorites`;
    const headers = {
        'Content-Type': 'application/json',
    };
    const results = useMutation({
        mutationKey: [url],
        mutationFn: async (favoriteInfo: ShippingPortalFavorite) => {
            const res = await AuthRequest(url, Method.POST, headers, favoriteInfo);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: false,
    });
    return results;
};

export const updateFavorite = () => {
    const url = `${baseUrl}/shippingPortal/favorites`;
    const headers = {
        'Content-Type': 'application/json',
    };
    const results = useMutation({
        mutationKey: [url],
        mutationFn: async (favoriteInfo: ShippingPortalFavorite) => {
            const { id, name, labelQuantity, dealerCount, parcelsToPrint } = favoriteInfo;

            const res = await AuthRequest(`${url}/${id}`, Method.PUT, headers, {
                name,
                labelQuantity,
                dealerCount,
                parcelsToPrint,
            });

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: false,
    });
    return results;
};

export const deleteFavorite = () => {
    const url = `${baseUrl}/shippingPortal/favorites`;
    const results = useMutation({
        mutationKey: [url],
        mutationFn: async (favoriteId: string) => {
            const res = await AuthRequest(`${url}/${favoriteId}`, Method.DELETE);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};

export const getPreferences = (userId: string, isEnabled = true) => {
    const url = `${baseUrl}/user/${userId}/preferences`;
    const results = useQuery({
        queryKey: [url, userId],
        queryFn: async () => {
            const res = await AuthRequest(url, Method.GET);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
        enabled: isEnabled,
    });
    return results;
};

export const updateOutboundPreferences = (userId: string) => {
    const url = `${baseUrl}/user/${userId}/preferences`;
    const headers = {
        'Content-Type': 'application/json',
    };
    const results = useMutation({
        mutationKey: [url, userId],
        mutationFn: async (
            preferences: Record<NestedKeyOf<UserPreferences>, string | string[] | ShippingPortalLabelSizes | undefined>,
        ) => {
            const res = await AuthRequest(`${url}`, Method.PUT, headers, {
                userId,
                preferences,
            });

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};

export const useQuickShip = () => {
    const url = `${baseUrl}/shippingPortal/quick-ship`;
    const headers = {
        'Content-Type': 'application/json',
    };

    return useMutation({
        mutationKey: [url],
        mutationFn: async ({
            parcelPayload,
            departureDate,
            note,
        }: {
            parcelPayload: ShippingPortalParcelWithLocationsAsIds;
            departureDate: string;
            note: string;
        }) => {
            const res = await AuthRequest(url, Method.POST, headers, {
                parcelPayload,
                departureDate,
                note,
            });

            if (res.ok) {
                const result = await res.json();
                return result;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
};
